import {
	COLLECTION_USER_SYNC_COOLDOWN_MS,
	DISCOGS_CACHE_FRESHNESS_MS,
} from '../../shared/config/constants'
import type { CollectionFilterQuery } from '../../shared/filters/collectionFilterQuery'
import type {
	ClientCollection,
	ClientRelease,
	CollectionMasterYearUpdate,
	CollectionStatusResponse,
	SyncJobSummary,
} from '../../shared/types/collection'
import {
	getCollectionStatus,
	getDiscogsCollectionPage,
	getSyncJob,
	startCollectionSync,
} from '../api/collection'
import {
	clearCollectionCache,
	getCachedCollection,
	getCachedReleaseByInstanceId as getPersistentCachedReleaseByInstanceId,
	getCachedReleaseByReleaseId as getPersistentCachedReleaseByReleaseId,
	setCachedCollection,
	updateCachedReleaseByInstanceId as updatePersistentCachedReleaseByInstanceId,
	updateCachedReleaseByReleaseId as updatePersistentCachedReleaseByReleaseId,
} from '../cache/collection'
import { delay } from '../utils/async'

export interface CollectionReadinessContext {
	ready: boolean
	revision: number
	snapshotId: string | null
	wasEmpty: boolean
}

type CollectionStorePhase =
	| 'blocked'
	| 'checking'
	| 'fresh'
	| 'idle'
	| 'syncing'

type CollectionStoreState = {
	activeJobId: string | null
	phase: CollectionStorePhase
	readiness: CollectionReadinessContext
	status: CollectionStatusResponse | null
}

type LoadCollectionPageArgs = {
	cacheKey: string
	filters: CollectionFilterQuery
	limit: number
	offset: number
	searchQuery: string
}

type CollectionStoreListener = (state: CollectionStoreState) => void
type CollectionMasterYearListener = (
	updates: CollectionMasterYearUpdate[],
) => void

// Revisions invalidate mounted views; cache generations only invalidate queries.
let collectionRevision = 0
let sessionGeneration = 0
let sessionController = new AbortController()
let cacheGeneration = 0
let foreground = true
let readinessRequest: Promise<CollectionReadinessContext> | null = null
let monitorRequest: Promise<void> | null = null
let expiryTimer: ReturnType<typeof setTimeout> | undefined
let state: CollectionStoreState = {
	activeJobId: null,
	phase: 'idle',
	readiness: notReady(false),
	status: null,
}

let statusRequest: Promise<CollectionStatusResponse> | null = null
let syncRequest: Promise<SyncJobSummary | null> | null = null
const jobRequests = new Map<string, Promise<SyncJobSummary | null>>()
const collectionPageRequests = new Map<
	string,
	Promise<ClientCollection | null>
>()
const collectionPageCache = new Map<string, ClientCollection>()
const listeners = new Set<CollectionStoreListener>()
const masterYearListeners = new Set<CollectionMasterYearListener>()

export function getCollectionStoreState(): CollectionStoreState {
	return state
}

export function subscribeCollectionStore(
	listener: CollectionStoreListener,
): () => void {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

export function subscribeCollectionMasterYearUpdates(
	listener: CollectionMasterYearListener,
): () => void {
	masterYearListeners.add(listener)
	return () => {
		masterYearListeners.delete(listener)
	}
}

export function applyCollectionMasterYearUpdates(
	updates: CollectionMasterYearUpdate[],
): void {
	const updatesByInstanceId = new Map(
		updates.map((update) => [Number(update.instanceId), update]),
	)
	const changedInstanceIds = new Set<number>()

	collectionPageCache.forEach((collection) => {
		collection.releases.forEach((release) => {
			const instanceId = Number(release.id)
			const update = updatesByInstanceId.get(instanceId)
			if (!update) return

			const currentYear =
				typeof release.data.master_year === 'number' &&
				release.data.master_year > 0
					? release.data.master_year
					: undefined
			if (
				currentYear === update.masterYear &&
				release.data.master_year_status === update.status
			) {
				return
			}

			if (update.masterYear) {
				release.data.master_year = update.masterYear
			} else {
				delete release.data.master_year
			}
			release.data.master_year_status = update.status
			changedInstanceIds.add(instanceId)
		})
	})

	const changedUpdates = updates.filter((update) =>
		changedInstanceIds.has(Number(update.instanceId)),
	)

	if (!changedUpdates.length) return
	masterYearListeners.forEach((listener) => listener(changedUpdates))
}

/** Invalidates query data without confusing a user mutation with snapshot expiry. */
export function invalidateCollectionState(): void {
	collectionRevision++
	clearCollectionQueryCache()
	setState({
		readiness: { ...state.readiness, revision: collectionRevision },
	})
}

export function resetCollectionSession(): void {
	sessionGeneration++
	sessionController.abort()
	sessionController = new AbortController()
	clearTimeout(expiryTimer)
	readinessRequest = null
	monitorRequest = null
	statusRequest = null
	syncRequest = null
	jobRequests.clear()
	foreground = document.visibilityState !== 'hidden'
	collectionRevision++
	clearCollectionQueryCache()
	setState({
		activeJobId: null,
		phase: 'idle',
		readiness: notReady(false),
		status: null,
	})
}

export function hasUsableCollection(): boolean {
	return state.readiness.ready && statusValidUntil(state.status) > Date.now()
}

function statusValidUntil(status: CollectionStatusResponse | null): number {
	if (!status) return 0
	return status.validUntil
		? Date.parse(status.validUntil)
		: Date.parse(status.syncedAt ?? '') + DISCOGS_CACHE_FRESHNESS_MS
}

function acceptStatus(status: CollectionStatusResponse): void {
	const usable =
		(status.usable ?? status.state === 'fresh') &&
		statusValidUntil(status) > Date.now()
	// The first status read establishes the baseline; it is not a data change.
	const changed =
		state.status !== null &&
		state.readiness.snapshotId !== (status.snapshotId ?? null)
	if (changed) {
		collectionRevision++
		clearCollectionQueryCache()
	}

	let phase: CollectionStorePhase = 'blocked'
	if (status.job) phase = 'syncing'
	else if (usable) phase = 'fresh'

	clearTimeout(expiryTimer)
	setState({
		status,
		activeJobId: status.job?.id ?? null,
		phase,
		readiness: {
			ready: usable,
			snapshotId: status.snapshotId ?? null,
			revision: collectionRevision,
			wasEmpty: state.readiness.wasEmpty || status.state === 'empty',
		},
	})
	if (usable)
		expiryTimer = setTimeout(
			expireCollection,
			Math.max(0, statusValidUntil(status) - Date.now()),
		)
}

export function expireCollection(): void {
	clearTimeout(expiryTimer)
	setState({ phase: 'idle', readiness: { ...state.readiness, ready: false } })
}

/** Drops query results without making other cached page views stale. */
export function clearCollectionQueryCache(): void {
	cacheGeneration++
	clearCollectionCache()
	collectionPageCache.clear()
	collectionPageRequests.clear()
}

export function getCachedReleaseByInstanceId(
	instanceId: number | string,
): ClientRelease | undefined {
	if (!hasUsableCollection()) return undefined
	const targetId = Number(instanceId)
	return (
		findMemoryCachedRelease((release) => Number(release.id) === targetId) ??
		getPersistentCachedReleaseByInstanceId(instanceId)
	)
}

export function getCachedReleaseByReleaseId(
	releaseId: number | string,
): ClientRelease | undefined {
	if (!hasUsableCollection()) return undefined
	const targetId = Number(releaseId)
	return (
		findMemoryCachedRelease(
			(release) => Number(release.data?.id) === targetId,
		) ?? getPersistentCachedReleaseByReleaseId(releaseId)
	)
}

export function updateCachedReleaseByInstanceId(
	release: ClientCollection['releases'][number],
	instanceId: number | string | null | undefined = release.id,
): ClientCollection['releases'][number] | undefined {
	const targetId = Number(instanceId)
	if (!Number.isFinite(targetId)) return release

	const memoryMerged = updateMemoryCachedRelease(release, (cachedRelease) => {
		return Number(cachedRelease.id) === targetId
	})
	const persistentMerged = updatePersistentCachedReleaseByInstanceId(
		release,
		instanceId,
	)
	return memoryMerged ?? persistentMerged
}

export function updateCachedReleaseByReleaseId(
	release: ClientCollection['releases'][number],
	releaseId: number | string | null | undefined = release.data?.id,
): ClientCollection['releases'][number] | undefined {
	const targetId = Number(releaseId)
	if (!Number.isFinite(targetId)) return release

	const memoryMerged = updateMemoryCachedRelease(release, (cachedRelease) => {
		return Number(cachedRelease.data?.id) === targetId
	})
	const persistentMerged = updatePersistentCachedReleaseByReleaseId(
		release,
		releaseId,
	)
	return memoryMerged ?? persistentMerged
}

export async function refreshCollectionStatus(): Promise<CollectionStatusResponse> {
	if (statusRequest) return statusRequest
	const generation = sessionGeneration
	const request = getCollectionStatus()
		.then((status) => {
			if (generation === sessionGeneration) acceptStatus(status)
			return status
		})
		.finally(() => {
			if (statusRequest === request) statusRequest = null
		})
	statusRequest = request
	return request
}

export async function loadCollectionPageFromStore({
	cacheKey,
	filters,
	limit,
	offset,
	searchQuery,
}: LoadCollectionPageArgs): Promise<ClientCollection | null> {
	if (!hasUsableCollection())
		throw new Error('Collection snapshot unavailable')
	const requestSnapshotId = state.readiness.snapshotId
	const requestRevision = state.readiness.revision
	const generation = cacheGeneration

	const memoryCached = collectionPageCache.get(cacheKey)
	if (memoryCached && isCollectionForCurrentSnapshot(memoryCached)) {
		return memoryCached
	}

	const persistentCached = getCachedCollection(cacheKey)
	if (persistentCached && isCollectionForCurrentSnapshot(persistentCached)) {
		collectionPageCache.set(cacheKey, persistentCached)
		return persistentCached
	}

	const pending = collectionPageRequests.get(cacheKey)
	if (pending) return pending

	const request = getDiscogsCollectionPage(offset, limit, {
		sort: filters.sort,
		favoritesOnly: filters.favoritesOnly,
		media: filters.media,
		rating: filters.rating,
		folderId: filters.folderId ?? undefined,
		status: filters.status,
		genre: filters.genre,
		style: filters.style,
		yearFrom: filters.yearFrom,
		yearTo: filters.yearTo,
		search: searchQuery || undefined,
	})
		.then((collection) => {
			if (
				!hasUsableCollection() ||
				generation !== cacheGeneration ||
				state.readiness.snapshotId !== requestSnapshotId ||
				state.readiness.revision !== requestRevision
			) {
				return null
			}
			if (!isCollectionForSnapshot(collection, requestSnapshotId)) {
				expireCollection()
				throw new Error('Collection snapshot changed')
			}

			collectionPageCache.set(cacheKey, collection)
			setCachedCollection(collection, cacheKey)
			return collection
		})
		.finally(() => {
			if (collectionPageRequests.get(cacheKey) === request) {
				collectionPageRequests.delete(cacheKey)
			}
		})

	collectionPageRequests.set(cacheKey, request)
	return request
}

/** Resolves null when the server reuses a just-synced snapshot. */
export async function startSharedCollectionSync(
	force = false,
): Promise<SyncJobSummary | null> {
	if (syncRequest) return syncRequest
	const generation = sessionGeneration
	const request = startCollectionSync(force)
		.then((job) => {
			if (generation !== sessionGeneration)
				throw new Error('Session ended')
			if (!job) return null
			setState({
				activeJobId: job.id,
				phase: 'syncing',
				status: state.status ? { ...state.status, job } : null,
			})
			return job
		})
		.finally(() => {
			if (syncRequest === request) syncRequest = null
		})
	syncRequest = request
	return request
}

/**
 * User-requested Discogs check; joins a running sync and skips recent snapshots.
 * Resolves false when no sync ran.
 */
export async function syncCollectionFromDiscogs(): Promise<boolean> {
	const syncedAt = Date.parse(state.status?.syncedAt ?? '')
	const recent = Date.now() - syncedAt < COLLECTION_USER_SYNC_COOLDOWN_MS
	if (!state.activeJobId && !syncRequest) {
		if (recent && hasUsableCollection()) return false
		if (!(await startSharedCollectionSync(true))) return false
	} else if (syncRequest && !(await syncRequest)) {
		return false
	}
	await monitorCollectionSync()
	return true
}

/** One monitor serves readiness, user refreshes, and background sync. */
export function monitorCollectionSync(): Promise<void> {
	if (monitorRequest) return monitorRequest
	const generation = sessionGeneration
	const signal = sessionController.signal
	let jobId = state.activeJobId
	const request = (async () => {
		while (generation === sessionGeneration) {
			if (!foreground) {
				await delay(1000, signal)
				continue
			}
			const job = jobId ? await getSharedSyncJob(jobId) : null
			if (generation !== sessionGeneration) return
			if (jobId && !job)
				throw new Error('Could not read collection sync job')
			if (job?.status === 'failed')
				throw new Error(job.message ?? 'Collection sync failed')
			const status = await refreshCollectionStatus()
			if (generation !== sessionGeneration) return
			jobId ??= status.job?.id ?? null
			if (job?.status === 'completed' || (!jobId && !status.job)) {
				if (!hasUsableCollection())
					throw new Error(
						'Collection sync did not produce a usable snapshot',
					)
				return
			}
			if (status.job?.status === 'failed')
				throw new Error(status.job.message ?? 'Collection sync failed')
			await delay(1000, signal)
		}
	})()
		.catch((error) => {
			if (generation === sessionGeneration)
				setState({ phase: 'blocked', activeJobId: null })
			throw error
		})
		.finally(() => {
			if (monitorRequest === request) monitorRequest = null
		})
	monitorRequest = request
	return request
}

async function getSharedSyncJob(jobId: string): Promise<SyncJobSummary | null> {
	const pending = jobRequests.get(jobId)
	if (pending) return pending

	const request = getSyncJob(jobId).finally(() => {
		if (jobRequests.get(jobId) === request) jobRequests.delete(jobId)
	})
	jobRequests.set(jobId, request)
	return request
}

/** Cached navigation is synchronous; only an unusable snapshot needs server work. */
export function ensureFreshCollection(): Promise<CollectionReadinessContext> {
	if (hasUsableCollection()) return Promise.resolve(state.readiness)
	if (readinessRequest) return readinessRequest
	const generation = sessionGeneration
	const request = (async () => {
		// Install readinessRequest before notifying subscribers via setState.
		await Promise.resolve()
		setState({ phase: 'checking' })
		const status = await refreshCollectionStatus()
		if (generation !== sessionGeneration) throw new Error('Session ended')
		if (hasUsableCollection()) {
			if (status.job) void monitorCollectionSync().catch(() => {})
			return state.readiness
		}
		if (!status.job) await startSharedCollectionSync()
		await monitorCollectionSync()
		if (generation !== sessionGeneration) throw new Error('Session ended')
		return state.readiness
	})()
		.catch((error) => {
			if (generation === sessionGeneration) setState({ phase: 'blocked' })
			throw error
		})
		.finally(() => {
			if (readinessRequest === request) readinessRequest = null
		})
	readinessRequest = request
	return request
}

export function setCollectionForeground(active: boolean): void {
	foreground = active
}

function notReady(wasEmpty: boolean): CollectionReadinessContext {
	return {
		ready: false,
		revision: collectionRevision,
		snapshotId: null,
		wasEmpty,
	}
}

function isCollectionForCurrentSnapshot(collection: ClientCollection): boolean {
	return (
		hasUsableCollection() &&
		isCollectionForSnapshot(collection, state.readiness.snapshotId)
	)
}

function isCollectionForSnapshot(
	collection: ClientCollection,
	snapshotId: string | null,
): boolean {
	const collectionSnapshotId = collection.snapshotId ?? snapshotId
	return !snapshotId || collectionSnapshotId === snapshotId
}

function findMemoryCachedRelease(
	matches: (release: ClientRelease) => boolean,
): ClientRelease | undefined {
	for (const collection of collectionPageCache.values()) {
		const release = collection.releases.find(matches)
		if (release) return release
	}
	return undefined
}

function updateMemoryCachedRelease(
	release: ClientCollection['releases'][number],
	matches: (release: ClientRelease) => boolean,
): ClientCollection['releases'][number] | undefined {
	let merged: ClientCollection['releases'][number] | undefined

	collectionPageCache.forEach((collection) => {
		const idx = collection.releases.findIndex(matches)
		if (idx === -1) return

		const cachedRelease = collection.releases[idx]
		merged = {
			...cachedRelease,
			...release,
			history: release.history ?? cachedRelease.history,
		}
		collection.releases[idx] = merged
		collection.timestamp = collection.timestamp ?? Date.now()
	})

	return merged
}

function setState(updates: Partial<CollectionStoreState>): void {
	state = {
		...state,
		...updates,
	}
	listeners.forEach((listener) => listener(state))
}
